import type { Borders, Fill, Worksheet } from "exceljs";
import {
  ReportColumnType,
  ReportPeriodicity,
  type IReportColumn,
  type IReportGrouping,
} from "../../../../models/report.dto";
import {
  buildReportSections,
  type IReportSection,
} from "./GroupedReportResultTable";
import {
  isNumericReportColumn,
  normalizeColumnType,
  resolveReportTable,
  type TReportTranslate,
} from "./ReportPrintableTable";
import type {
  IReportFilterSummaryItem,
  IReportTableRow,
} from "./reportExecutionUtils";

interface IExportReportToExcelParams {
  title: string;
  subtitle: string;
  sheetName: string;
  fileName: string;
  filterItems: IReportFilterSummaryItem[];
  columns: IReportColumn[];
  rows: IReportTableRow[];
  periodicity?: ReportPeriodicity | null;
  grouping?: IReportGrouping | null;
  t: TReportTranslate;
  language: string;
}

const HEADER_FILL: Fill = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFDDE1E6" },
};
const ZEBRA_FILL: Fill = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFEBEDF0" },
};
const STRONG_BORDER_COLOR = { argb: "FF9CA3AF" };
const HEADER_BORDER: Partial<Borders> = {
  bottom: { style: "medium", color: STRONG_BORDER_COLOR },
};
const TOTAL_BORDER: Partial<Borders> = {
  top: { style: "medium", color: STRONG_BORDER_COLOR },
};

const MIN_COLUMN_WIDTH = 10;
const MAX_COLUMN_WIDTH = 60;

const getNumberFormat = (column: IReportColumn) => {
  const columnType = normalizeColumnType(column.type);

  if (columnType === ReportColumnType.Currency) {
    return column.format?.trim().toUpperCase() === "USD"
      ? '"$" #,##0.00'
      : '"R$" #,##0.00';
  }

  if (columnType === ReportColumnType.Percentage) {
    return '#,##0.##"%"';
  }

  return "#,##0";
};

const toNumber = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
};

const writeSection = (
  sheet: Worksheet,
  section: IReportSection,
  params: IExportReportToExcelParams,
  columnWidths: number[],
) => {
  const { visibleColumns, visibleRows, getColumnLabel, getDisplayValue } =
    resolveReportTable({
      columns: params.columns,
      rows: section.rows,
      periodicity: params.periodicity,
      showTotals: section.showTotals,
      summaryOnly: section.summaryOnly,
      t: params.t,
      language: params.language,
    });

  if (section.title) {
    const titleRow = sheet.addRow([section.title]);
    titleRow.font = { bold: true, size: 12 };
  }

  const headerRow = sheet.addRow(visibleColumns.map(getColumnLabel));
  headerRow.eachCell((cell, columnNumber) => {
    const column = visibleColumns[columnNumber - 1];

    cell.font = { bold: true };
    cell.fill = HEADER_FILL;
    cell.border = HEADER_BORDER;
    cell.alignment = {
      horizontal: isNumericReportColumn(column) ? "right" : "left",
    };
  });

  visibleRows.forEach((row, rowIndex) => {
    const values = visibleColumns.map((column, columnIndex) => {
      const numericValue = isNumericReportColumn(column)
        ? toNumber(row.values[column.key])
        : undefined;

      return numericValue ?? getDisplayValue(row, column, columnIndex);
    });
    const excelRow = sheet.addRow(values);

    excelRow.eachCell({ includeEmpty: true }, (cell, columnNumber) => {
      const column = visibleColumns[columnNumber - 1];

      if (!column) return;

      if (isNumericReportColumn(column)) {
        cell.numFmt = getNumberFormat(column);
        cell.alignment = { horizontal: "right" };
      }

      if (row.isTotal) {
        cell.font = { bold: true };
        cell.border = TOTAL_BORDER;
      } else if (rowIndex % 2 === 1) {
        cell.fill = ZEBRA_FILL;
      }
    });
  });

  visibleColumns.forEach((column, columnIndex) => {
    const texts = [
      getColumnLabel(column),
      ...visibleRows.map((row) => getDisplayValue(row, column, columnIndex)),
    ];
    const longest = Math.max(...texts.map((text) => String(text).length));

    columnWidths[columnIndex] = Math.max(
      columnWidths[columnIndex] ?? MIN_COLUMN_WIDTH,
      Math.min(longest + 2, MAX_COLUMN_WIDTH),
    );
  });

  sheet.addRow([]);
};

export const exportReportToExcel = async (
  params: IExportReportToExcelParams,
) => {
  const { Workbook } = await import("exceljs");
  const workbook = new Workbook();
  const sheet = workbook.addWorksheet(params.sheetName.slice(0, 31));

  const titleRow = sheet.addRow([params.title]);
  titleRow.font = { bold: true, size: 14 };

  const subtitleRow = sheet.addRow([params.subtitle]);
  subtitleRow.font = { color: { argb: "FF6B7280" } };

  sheet.addRow([]);

  params.filterItems.forEach((item) => {
    const filterRow = sheet.addRow([`${item.label}:`, item.value]);
    filterRow.getCell(1).font = { bold: true };
  });

  sheet.addRow([]);

  const sections: IReportSection[] = params.grouping
    ? buildReportSections(
        params.columns,
        params.rows,
        params.grouping,
        params.t,
        params.language,
      )
    : [
        {
          key: "__report__",
          title: "",
          rows: params.rows,
          showTotals: false,
          summaryOnly: false,
        },
      ];

  const columnWidths: number[] = [
    Math.max(
      MIN_COLUMN_WIDTH,
      ...params.filterItems.map((item) => item.label.length + 2),
    ),
  ];

  sections.forEach((section) =>
    writeSection(sheet, section, params, columnWidths),
  );

  columnWidths.forEach((width, index) => {
    sheet.getColumn(index + 1).width = width;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = params.fileName;
  link.click();
  URL.revokeObjectURL(url);
};
